import type {
  Action,
  MutationUpdateNetworkStatusArgs,
  Network,
} from '@tribeplatform/gql-client/types'

import {
  useMutation,
  UseMutationOptions,
} from '../../lib/react-query/useMutation.js'
import { useQueryClient } from '../../lib/react-query/useQueryClient.js'
import { useTribeClient } from '../../useTribeClient.js'
import { getAuthTokenKey } from '../../utils/keys/authToken.keys.js'
import { getNetworkKey } from '../../utils/keys/network.key.js'

export const useUpdateNetworkStatus = (options?: {
  useMutationOptions?: UseMutationOptions<
    Action,
    Error,
    MutationUpdateNetworkStatusArgs
  >
}) => {
  const { useMutationOptions } = options || {}
  const { client } = useTribeClient()
  const queryClient = useQueryClient()
  const networkKey = getNetworkKey()
  const authTokenKey = getAuthTokenKey()

  return useMutation<Action, Error, MutationUpdateNetworkStatusArgs>(
    (variables: MutationUpdateNetworkStatusArgs) =>
      client.mutation({
        name: 'updateNetworkStatus',
        args: {
          fields: 'all',
          variables,
        },
      }),
    {
      ...useMutationOptions,
      onMutate: variables => {
        queryClient.setQueriesData<Network>(networkKey, old => ({
          ...old,
          status: variables.input.status,
        }))
        useMutationOptions?.onMutate?.(variables)
      },
      onSettled: (data, error, variables, context) => {
        // Re-fetch the network
        queryClient.invalidateQueries(networkKey)

        // Re-fetch the auth tokens
        queryClient.invalidateQueries(authTokenKey)
        useMutationOptions?.onSettled?.(data, error, variables, context)
      },
    },
  )
}
